<template>
  <BaseWidget
    :class="['notifications-timeline']"
    :loading="loading"
    :widget-options="widgetMenuOptions" 
    :override-options="true"
    @refresh-data="refreshData" 
    @set-config="setConfig"
    v-on="$listeners"
    :content-only="mainTop"
  >
    <template v-if="!mainTop" v-slot:header>
      <div class="title">
        <span class="title-icon">
        </span>
        {{ title }}
      </div>
      <div class="description">{{ filterText }}</div>
    </template>
    <template v-else v-slot:header>
      <div class="title">
        {{ title }}
      </div>
      <div class="top-notifications-head">
        <button @click.prevent="updateFilters('unseen')" ref="not-btn-unseen" class="notification-btn-link active">{{ $gettext('New') }} ({{ newCount }})</button>
        <button @click.prevent="updateFilters('seen')" ref="not-btn-seen" class="notification-btn-link">{{ $gettext('Seen') }} ({{ seenCount }})</button>
        <!-- <button @click.prevent="updateFilters('all')" ref="not-btn-all" class="notification-btn-link">All notifications</button> -->
      </div>
    </template>
    <template v-slot:content>
      <div class="list-wrap">
        <ul role="list" v-if="!loading && (inviteItems.length > 0 || notificationItems.length > 0)" class="notification-list">
          <InvitationItem v-for="(item, index) in inviteItems" :key="'inv_' + item.id + '-' + index" :item="item" />
          <NotificationItem v-for="(item, index) in notificationItems" :key="'not_' + item.id + '-' + index" :item="item" />
        </ul>
        <a v-if="nextUrl" class="load-more-btn" href="#" @click.prevent="loadMore()">{{ $gettext('Load more') }}</a>
        <span v-if="!nextUrl && (notificationItems && notificationItems.length > 0)" class="load-more-btn all-items">{{ $gettext('Displaying all notifications') }}</span>
        <div v-if="isEmpty" class="list-status-empty">
          <AnimationItem  class="anim-calm" :animation="{ animationData: calmAnimation.default }" :key="'anim_success'" />
          {{ $gettext('Your inbox is empty.') }}
        </div>
      </div>
    </template>
  </BaseWidget>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DashboardService from "@/store/services/dashboard.service.js";
import BaseWidget from "@/components/dashboard/widgets/BaseWidget"
import InvitationItem from "@/components/dashboard/widgets/InvitationItem"
import NotificationItem from "@/components/dashboard/widgets/NotificationItem"
import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'
import * as calmAnimation from '@/css/animations/calm.json';

export default {
  name: 'notifications-widget',
  components: { BaseWidget, NotificationItem, InvitationItem, AnimationItem },
  props: {
    widget: {
      type: Object,
      default: () => {}
    },
    mainTop: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: true,
      title: '',
      subtitle: '',
      notificationItems: null,
      // inviteItems: null,
      allItems: [],
      nextUrl: null,
      config: { status: '' },
      checkInterval: null,
      calmAnimation: calmAnimation,
      filters: {
        object_type: '',
        created_by_in: '',
        type: 'notification',
        company_profile: window.company_id
      }
    }
  },
  computed: {
    isEmpty () {
      return (this.notificationItems === null || this.notificationItems.length === 0) && (this.inviteItems === null || this.inviteItems.length === 0)
    },
    widgetMenuOptions () {
      if (this.loading) return []
      return [
        { name: this.$gettext('All notifications'), event: { name: 'set-config', config: { status: 'all' } } },
        { name: this.$gettext('Seen'), event: { name: 'set-config', config: { status: 'seen' } } },
        { name: this.$gettext('Unseen'), event: { name: 'set-config', config: { status: 'unseen' } } },
        // { name: this.$gettext('Pending'), event: { name: 'set-config', config: { status: 'pending' } } },
        { separator: true },
        { name: this.$gettext('Remove from dashboard'), icon: 'icon-remove', event: { name: 'remove' } },
        { separator: true },
        { name: this.$gettext('Refresh data'), icon: 'icon-refresh', event: { name: 'refresh' } }
      ]
    },
    filterText () {
      let text = this.$gettext('All notifications')
      if (this.config.status !== '') {
        text = this.$gettext('Showing only')
        switch (this.config.status) {
          case 'seen':
            text = text + ' ' + this.$gettext('seen notifications')
            break;
          case 'unseen':
            text = text + ' ' + this.$gettext('unseen notifications')
            break;
          case 'pending':
            text = text + ' ' + this.$gettext('pending notifications')
            break;
        }
      }
      return text
    },
    newCount () {
      const res = this.allItems.reduce((obj, v) => {
        obj[v.status] = (obj[v.status] || 0) + 1;
        return obj;
      }, {})
      return (res['unseen']) ? res['unseen'] : 0
    },
    seenCount () {
      const res = this.allItems.reduce((obj, v) => {
        obj[v.status] = (obj[v.status] || 0) + 1;
        return obj;
      }, {})
      return (res['seen']) ? res['seen'] : 0
    },
    ...mapGetters({ 
      getGekkoNotification: 'notifications/getGekkoNotification',
      getNotificationItems: 'notifications/getNotificationItems',
      getAllNotificationItems: 'notifications/getAllNotificationItems',
      inviteItems: 'notifications/getInviteItems',
    }),
  },
  created () {
    this.unsubscribe = this.$store.subscribe((mutation) => {
      if (mutation.type === 'notifications/NOTIFICATION_LIST_REFRESH') {
        // console.log('NOTIFICATION_LIST_REFRESH')
        this.refreshData()
      }
    })
  },
  mounted () {
    if(this.mainTop) { 
      // main notification widget (top navbar)
      // checking for new notifications each 120 seconds
      this.checkNewNotificationsCount()
      this.checkInterval = setInterval(() => {
        this.checkNewNotificationsCount()
      }, 120 * 1000);
    }
    this.setupWidget()
    this.setObjectStatus(this.widget.config)
  },
  beforeDestroy() {
    this.unsubscribe()
    clearInterval(this.checkInterval)
  },
  methods: {
    ...mapActions({ 
      loadNotificationTemplate: 'notifications/loadNotificationTemplate',
      checkNewNotificationsCount: 'notifications/checkForNotificationsCount',
      loadAllNotifications: 'notifications/loadAllNotifications'
    }),
    setConfig (config) {
      if (config.status) this.setObjectStatus(config)
      this.$emit('save-config', this.config)
    },
    setObjectStatus (config) {
      if (config.status) {
        this.config.status = (config.status && config.status !== 'all') ? config.status : ''
      } 
      this.setFilters()
    },
    setFilters (status = false) {
      this.filters.status = (status) ? (status === 'all') ? '' : status : this.config.status
      if (this.mainTop) this.checkActiveFilterButton()
      // console.log('filters set, first data load')
      this.refreshData()
    },
    refreshData () {
      this.getData()
    },
    updateFilters (status) {
      this.filters.status = (status === 'all') ? '' : status
      this.setFilters(status)
    },
    checkActiveFilterButton () {
      const status = (this.filters.status === '') ? 'all' : this.filters.status
      this.$refs['not-btn-unseen'].classList.remove('active')
      this.$refs['not-btn-seen'].classList.remove('active')
      // this.$refs['not-btn-all'].classList.remove('active')
      this.$refs['not-btn-' + status].classList.add('active')
    },
    getData () {
      this.loading = true
      this.nextUrl = null
      this.loadAllNotifications().then((response) => {
        this.nextUrl = response
        this.loading = false
        this.allItems = this.getAllNotificationItems
        this.notificationItems = (this.filters.status === '') 
          ? this.getAllNotificationItems
          : this.getNotificationItems(this.filters.status)
      })
    },
    loadMore () {
      this.loading = true
      DashboardService.moreActivities(this.nextUrl).then((response) => {
        this.nextUrl = response.data.next
        this.notificationItems = this.notificationItems.concat(response.data.results)
        this.loading = false
      })
    },
    setupWidget () {
      this.title = this.$gettext('Notifications')
    }
  }
}
</script>

<style lang="scss">
.notifications-timeline {
  overflow: hidden;
  height: 100%;
  margin-bottom: 20px;
  .list-wrap { 
    flex-direction: column;
    margin: -15px;
    flex: 1;
  }
  ul.notification-list {
    margin: 0;
    padding: 0;
    list-style: none;

    background: white;
    padding: 7px;
  }
  .load-more-btn {
    display: block;
    text-align: center;
    padding: 10px;
    padding-bottom: 20px;
    font-size: 14px;
    &.all-items { color: #cacaca; }
  }
  .list-status-empty {
    text-align: center;
    font-size: 16px;
    color: #0000007d;
    width: 100%;
  }
  .anim-calm {
    height: 150px !important;
  }
}
.top-notifications-head {
  margin-bottom: -8.5px;
  // margin-left: -6px;
  // margin-right: -6px;
  .notification-btn-link {
    display: inline-block;
    margin-right: 10px;
    background: none;
    border: none;
    color: #c5c5c5;
    font-size: 14px;
    border-bottom: 2px solid transparent;
    &.active {
      color: $colorTextBase;
      border-bottom: 2px solid currentColor;
    }
    &:last-child {
      margin-right: 0px;
    }
  }
}


</style>